/* MoleculeSketcher.jsx
 *
 * INLINE panel for drawing the Molexar task input structure (an alternative to
 * typing start_smiles). Rendered inside CompoundGenerationTab -- NOT as a
 * ModalManager modal: that manager shows one modal at a time, so opening it would
 * replace/unmount the compound-generation dialog and drop the result. Inline keeps
 * the tab mounted, so onAccept's setState lands correctly.
 *
 * The drawing is extracted as an MDL molfile via Ketcher's getMolfile(); the Molexar
 * backend (molexar_run.py) converts it to SMILES with full RDKit -- preserving [*]
 * attachment points and multiple '.'-separated fragments (linker_design /
 * scaffold_morphing). No browser SMILES export is used (Indigo is down here;
 * MinimalLib strips chirality).
 *
 * proto: src/MoleculeSketcher.jsx (next to Ketcher.jsx).
 */
import React, { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Ketcher } from './Ketcher';
import { DialogSubmitButton, DialogCancelButton } from './ui/common/DialogActionButtons';

export function MoleculeSketcher({ onAccept, onClose }) {
    const ketcherRef = useRef(null);
    const [warn, setWarn] = useState('');

    async function handleAdd() {
        const mol = await ketcherRef.current?.getDrawnMolfile();
        if (!mol || !mol.includes('V2000')) {
            setWarn('Draw a structure first.');
            return;
        }
        onAccept(mol);
        onClose();
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '460px',
                gap: 1,
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
            }}
        >
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Draw your input structure. Add a * attachment point where the task should
                act (scaffold decoration / motif extension), or draw two *-tagged fragments
                for linker design and scaffold morphing. Superstructure needs no attachment point.
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <Ketcher ref={ketcherRef} drawMode iframeId="mol-ketcher" />
            </Box>
            {warn && (
                <Typography variant="body2" sx={{ color: 'warning.main' }}>
                    {warn}
                </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <DialogCancelButton onClick={onClose}>
                    Cancel
                </DialogCancelButton>
                <DialogSubmitButton onClick={handleAdd}>
                    Use this structure
                </DialogSubmitButton>
            </Box>
        </Box>
    );
}
